import React, { useState } from 'react';
import { Box, IconButton, MobileStepper } from '@mui/material';
import { KeyboardArrowLeft, KeyboardArrowRight } from '@mui/icons-material';

const ImageCarousel = ({ 
  imageUrls, 
  productName = '', 
  height = 160, 
  objectFit = 'contain',
  sx = {},
  onImageError = null
}) => {
  const [activeStep, setActiveStep] = useState(0);
  
  // Fallback SVG
  const fallbackSvg = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjIwMCIgaGVpZ2h0PSIyMDAiIGZpbGw9IiNFRUVFRUUiLz48dGV4dCB4PSI1MCUiIHk9IjUwJSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZmlsbD0iIzY2NiIgZm9udC1mYW1pbHk9IkFyaWFsIiBmb250LXNpemU9IjE0Ij5JbW1hZ2luZSBub24gZGlzcG9uaWJpbGU8L3RleHQ+PC9zdmc+';
  
  // Processa gli URL delle immagini
  const processImageUrls = (urls) => {
    // Gestione dei casi null/undefined/stringa vuota
    if (!urls || urls === 'null' || urls === 'undefined' || urls === '') {
      return [fallbackSvg];
    }
    
    let processedInput = urls;
    
    // Se l'immagine è un oggetto JSON, estraiamo l'URL
    if (typeof urls === 'object' && !Array.isArray(urls)) {
      processedInput = urls.text || urls.hyperlink || urls.value || urls.url || 
                     urls.path || urls.src || urls.image || urls.imageUrl || '';
    }
    
    // Converti in stringa se necessario
    processedInput = String(processedInput || '').trim();
    
    // Se dopo la conversione è vuoto, ritorna fallback
    if (!processedInput) {
      return [fallbackSvg];
    }
    
    let urlArray = [];
    if (typeof processedInput === 'string') {
      // Separa gli URL dal ";" e rimuovi spazi
      urlArray = processedInput.split(';').map(url => url.trim()).filter(url => url.length > 0);
    } else if (Array.isArray(processedInput)) {
      urlArray = processedInput;
    } else {
      urlArray = [String(processedInput)];
    }
    
    // Se non ci sono URL validi, ritorna il fallback
    if (urlArray.length === 0) {
      return [fallbackSvg];
    }
    
    // Processa ogni URL
    return urlArray.map((url) => {
      // Rimuovi spazi e caratteri non validi
      url = String(url).replace(/\s+/g, '');
      
      // Gestione speciale per RMS
      if (url.includes('images.rms.it')) {
        url = url.replace(/\s+/g, '-');
        if (!url.startsWith('http')) {
          url = 'https://' + url;
        }
        try {
          new URL(url);
          return url;
        } catch (e) {
          console.error('ImageCarousel - URL RMS non valido:', url);
          return fallbackSvg;
        }
      }
      
      // Gestione URL completi
      if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('data:')) {
        return url;
      }
      
      // Gestione percorsi locali
      if (url.startsWith('/uploads')) {
        const fullUrl = `${window.location.origin}${url}`;
        return fullUrl;
      }
      
      // Gestione percorsi relativi
      if (!url.startsWith('/')) {
        const fullUrl = `${window.location.origin}/uploads/products/${url}`;
        return fullUrl;
      }
      
      return url;
    });
  };
  
  const processedUrls = processImageUrls(imageUrls);
  const maxSteps = processedUrls.length;
  
  const handleNext = (e) => {
    e.stopPropagation(); // Evita che il click si propaghi al parent
    setActiveStep((prevActiveStep) => (prevActiveStep + 1) % maxSteps);
  };
  
  const handleBack = (e) => {
    e.stopPropagation(); // Evita che il click si propaghi al parent
    setActiveStep((prevActiveStep) => (prevActiveStep - 1 + maxSteps) % maxSteps);
  };
  
  const handleImageError = (e) => {
    console.warn(`ImageCarousel - Errore nel caricamento dell'immagine: ${processedUrls[activeStep]}`);
    e.target.src = fallbackSvg;
    if (onImageError) {
      onImageError(e);
    }
  };

  return (
    <Box sx={{ position: 'relative', ...sx }}>
      <img
        src={processedUrls[activeStep]}
        alt={productName}
        style={{
          width: '100%',
          height: height,
          objectFit: objectFit,
          padding: '8px',
          backgroundColor: '#f5f5f5',
          borderRadius: '4px'
        }}
        onError={handleImageError}
      />
      
      {/* Controlli navigation solo se ci sono più immagini */}
      {maxSteps > 1 && (
        <>
          {/* Pulsanti di navigazione */}
          <Box sx={{
            position: 'absolute',
            top: '50%',
            left: 0,
            right: 0,
            display: 'flex',
            justifyContent: 'space-between',
            transform: 'translateY(-50%)',
            px: 1
          }}>
            <IconButton
              size="small"
              onClick={handleBack}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>
            
            <IconButton
              size="small"
              onClick={handleNext}
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                }
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
          
          {/* Indicatori dots */}
          <MobileStepper
            steps={maxSteps}
            position="static"
            activeStep={activeStep}
            sx={{
              position: 'absolute',
              bottom: 0,
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: 'transparent',
              '& .MuiMobileStepper-dot': {
                backgroundColor: 'rgba(255, 255, 255, 0.5)',
              },
              '& .MuiMobileStepper-dotActive': {
                backgroundColor: 'white',
              }
            }}
            nextButton={<div />}
            backButton={<div />}
          />
        </>
      )}
    </Box>
  );
};

export default ImageCarousel; 